import React, { useEffect, useRef, useState } from 'react';
import { X, Upload, Github, ChevronRight, ArrowLeft, Send, Loader } from 'lucide-react';
import { userSkillsAPI } from '../../services/api';

// ─── Theme ────────────────────────────────────────────────────────────────────
const T = {
  bg:          'var(--lyra-color-surface)',
  surface:     'var(--lyra-color-surface-2)',
  border:      'var(--lyra-color-border)',
  borderHover: 'var(--app-border-strong)',
  ink:         'var(--lyra-color-text)',
  ink2:        'var(--app-text-2)',
  ink4:        'var(--lyra-color-text-muted)',
  ink6:        'var(--app-text-3)',
  green:       'var(--lyra-color-success)',
  greenDark:   '#1B5E45',
  greenLight:  'var(--app-primary-soft)',
  greenText:   '#085041',
  amber:       'var(--lyra-color-warning-bg)',
  amberText:   '#633806',
  red:         'var(--lyra-color-danger-bg)',
  redText:     '#712B13',
};

// ─── Skill creator definitions ────────────────────────────────────────────────
const CREATORS = [
  {
    id: 'claude',
    label: 'Claude',
    icon: '✦',
    iconBg: 'var(--app-surface-3)',
    iconColor: 'var(--lyra-color-primary)',
    desc: 'Iterative skill builder with eval loop',
    style: 'Conversational · thorough',
    systemPrompt: `You are a skill creation assistant following the Claude SKILL.md format.
A skill has YAML frontmatter (name, description) and a markdown body with instructions.
Start by asking: "What should this skill do?" then guide the user through:
1. Capturing intent and trigger conditions
2. Defining expected outputs
3. Writing the SKILL.md draft
4. Suggesting test cases
Be conversational. Adapt to technical level. Ask one question at a time.
When ready, output the final SKILL.md wrapped in triple backticks with md language tag.`,
  },
  {
    id: 'codex',
    label: 'Codex',
    icon: '</>',
    iconBg: 'var(--app-surface-3)',
    iconColor: '#1B5E45',
    desc: 'Structured skill scaffolding with validation',
    style: 'Structured · script-friendly',
    systemPrompt: `You are a skill creation assistant following the Codex SKILL.md format.
A skill has: YAML frontmatter (name, description only), markdown body, and optional scripts/references/assets folders.
Start by asking: "What should this skill do?" then guide through:
1. Concrete usage examples
2. Identifying reusable scripts or references needed
3. Writing lean SKILL.md (Codex is already capable — only add what it doesn't know)
4. Suggesting validation steps
Keep responses concise. Use imperative form in instructions. One question at a time.
When ready, output the final SKILL.md wrapped in triple backticks with md language tag.`,
  },
  {
    id: 'gemini',
    label: 'Gemini',
    icon: '◆',
    iconBg: 'var(--app-primary-soft)',
    iconColor: '#1565C0',
    desc: 'Multi-modal skill design with examples',
    style: 'Example-driven · concise',
    systemPrompt: `You are a skill creation assistant following the Gemini SKILL.md format.
A skill has YAML frontmatter (name, description) and a markdown body.
Start by asking: "What should this skill do?" then guide through:
1. What the skill enables
2. When it should trigger (specific phrases/contexts)
3. Expected input/output format
4. Key examples to include in the skill body
Be example-driven. Show concrete input/output pairs. One question at a time.
When ready, output the final SKILL.md wrapped in triple backticks with md language tag.`,
  },
  {
    id: 'openclaw',
    label: 'OpenClaw',
    icon: '⚡',
    iconBg: 'var(--lyra-color-warning-bg)',
    iconColor: '#E65100',
    desc: 'Minimal, strict skill with extended frontmatter',
    style: 'Minimal · local-first',
    systemPrompt: `You are a skill creation assistant following the OpenClaw SKILL.md format.
OpenClaw skills are minimal. Frontmatter supports: name, description (quoted), metadata, homepage, allowed-tools, user-invocable, license.
Start by asking: "What should this skill do?" then guide through:
1. Core action (one clear verb phrase)
2. Trigger conditions (short, generic)
3. Hard rules (syntax, caveats, safety)
4. Minimal body — fragments ok, no generic advice
Keep everything lean. Remove anything the base model already knows. One question at a time.
When ready, output the final SKILL.md wrapped in triple backticks with md language tag.`,
  },
];

// ─── LLM call (tries providers in order, fallback last) ──────────────────────
async function callLLM(
  messages: { role: string; content: string }[],
  systemPrompt: string,
): Promise<string> {
  try {
    const res = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 1000,
        system: systemPrompt,
        messages,
      }),
    });
    if (res.ok) {
      const data = await res.json();
      const text = data.content?.find((b: any) => b.type === 'text')?.text;
      if (text) return text;
    }
  } catch {}

  // Fallback: rule-based stub so the flow never breaks
  const last = messages[messages.length - 1]?.content || '';
  if (messages.length <= 2) {
    return "Great! To get started — what's the **main action** this skill should perform? For example: 'Review pull requests for security issues' or 'Write investor update emails'.";
  }
  if (messages.length <= 4) {
    return "Got it. And when should this skill activate? What would a user type that should trigger it? Give me a couple of example phrases.";
  }
  if (messages.length <= 6) {
    return "Perfect. What should the output look like — a written document, code, a summary, something else?";
  }
  // Build a minimal SKILL.md from conversation context
  const nameGuess = last.split(' ').slice(0, 3).join('-').toLowerCase().replace(/[^a-z0-9-]/g, '') || 'my-skill';
  return `Here's a draft SKILL.md based on our conversation:\n\n\`\`\`md\n---\nname: ${nameGuess}\ndescription: "${last.slice(0, 120).replace(/"/g, "'")}"\n---\n\n# ${nameGuess}\n\n## When to use\n\nUse this skill when the user asks to ${last.slice(0, 80)}.\n\n## Steps\n\n1. Understand the user's request\n2. Follow the workflow below\n3. Produce the expected output\n\n## Output format\n\nProvide a clear, structured response.\n\`\`\`\n\nFeel free to edit this — or paste it as-is to save the skill.`;
}

// ─── Parse SKILL.md from LLM response ─────────────────────────────────────────
function extractSkillMd(text: string): string | null {
  const match = text.match(/```(?:md|markdown)?\n([\s\S]+?)```/);
  return match ? match[1].trim() : null;
}

function parseSkillMdFields(raw: string): { name: string; description: string; body: string } {
  const parts = raw.split('---');
  if (parts.length < 3) return { name: '', description: '', body: raw };
  const fm = parts[1];
  const body = parts.slice(2).join('---').trim();
  const nameMatch = fm.match(/name:\s*(.+)/);
  const descMatch = fm.match(/description:\s*["']?(.+?)["']?\s*$/m);
  return {
    name: nameMatch?.[1]?.trim() || '',
    description: descMatch?.[1]?.trim() || '',
    body: raw,
  };
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface AddSkillModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (skill: any) => void;
  onDeleted?: (id: string) => void;
  initial?: any;
}

type Screen = 'menu' | 'chat' | 'upload' | 'github' | 'review';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

// ─── Subcomponents ─────────────────────────────────────────────────────────────

function IconBox({ creator }: { creator: typeof CREATORS[0] }) {
  return (
    <div style={{
      width: 32, height: 32, borderRadius: 8, flexShrink: 0,
      background: creator.iconBg, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: 13, fontWeight: 700, color: creator.iconColor,
      fontFamily: 'monospace',
    }}>
      {creator.icon}
    </div>
  );
}

function Divider() {
  return <div style={{ borderTop: `0.5px solid ${T.border}`, margin: '4px 0' }} />;
}

// ─── Stable subcomponents — MUST be defined outside the main component
// Defining them inside causes re-mount on every state change, dropping input focus

// ── Shared header ──────────────────────────────────────────────────────────
const Header = ({ title, onBack, onClose }: { title: string; onBack?: () => void; onClose?: () => void }) => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: `0.5px solid ${T.border}`,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {onBack && (
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.ink4, padding: '2px 4px', display: 'flex' }}>
          <ArrowLeft size={16} />
        </button>
      )}
      <span style={{ fontSize: 14, fontWeight: 500, color: T.ink }}>{title}</span>
    </div>
    {onClose ? (
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.ink6, display: 'flex' }}>
        <X size={16} />
      </button>
    ) : null}
  </div>
);
 // ── Overlay wrapper ────────────────────────────────────────────────────────
const Modal = ({ children, wide }: { children: React.ReactNode; wide?: boolean }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
  }}>
    <div style={{
      background: T.bg, borderRadius: 16, width: '100%',
      maxWidth: wide ? 640 : 480,
      maxHeight: '88vh', display: 'flex', flexDirection: 'column',
      boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      margin: '0 16px', overflow: 'hidden',
    }}>
      {children}
    </div>
  </div>
);



// ─── Main Modal ───────────────────────────────────────────────────────────────
const AddSkillModal: React.FC<AddSkillModalProps> = ({ isOpen, onClose, onCreated, initial }) => {
  const [screen, setScreen] = useState<Screen>('menu');
  const [activeCreator, setActiveCreator] = useState<typeof CREATORS[0] | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [llmLoading, setLlmLoading] = useState(false);
  const [extractedMd, setExtractedMd] = useState<string | null>(null);

  // Review/save state
  const [reviewName, setReviewName] = useState('');
  const [reviewDesc, setReviewDesc] = useState('');
  const [reviewBody, setReviewBody] = useState('');
  const [reviewDomain, setReviewDomain] = useState('');
  const [reviewTags, setReviewTags] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tagList, setTagList] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Upload state
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // GitHub state
  const [ghUrl, setGhUrl] = useState('');
  const [ghLoading, setGhLoading] = useState(false);
  const [ghError, setGhError] = useState<string | null>(null);

  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      setScreen(initial ? 'review' : 'menu');
      if (initial) prefillReview(initial.body || '');
      setMessages([]);
      setInput('');
      setExtractedMd(null);
      setSaveError(null);
    }
  }, [isOpen, initial]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  function prefillReview(raw: string) {
    const parsed = parseSkillMdFields(raw);
    setReviewName(initial?.name || parsed.name);
    setReviewDesc(initial?.description || parsed.description);
    setReviewBody(raw);
    setReviewDomain(initial?.domain || '');
    const t = initial?.tags || [];
    setTagList(Array.isArray(t) ? t : t.split(',').map((x: string) => x.trim()).filter(Boolean));
  }

  // ── Chat with creator ──────────────────────────────────────────────────────
  async function startCreator(creator: typeof CREATORS[0]) {
    setActiveCreator(creator);
    setMessages([]);
    setExtractedMd(null);
    setScreen('chat');
    setLlmLoading(true);

    const systemMsg = { role: 'system', content: creator.systemPrompt };
    const openingUser = { role: 'user', content: `Help me create a skill using the ${creator.label} skill format. First ask me what the skill should do.` };

    try {
      const reply = await callLLM([openingUser], creator.systemPrompt);
      setMessages([{ role: 'assistant', content: reply }]);
    } catch {
      setMessages([{ role: 'assistant', content: `Hi! I'm ready to help you create a ${creator.label}-style skill. What should this skill do?` }]);
    }
    setLlmLoading(false);
  }

  async function sendMessage() {
    if (!input.trim() || llmLoading || !activeCreator) return;
    const userMsg: Message = { role: 'user', content: input.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setLlmLoading(true);

    const apiMessages = [
      { role: 'user', content: `[System: ${activeCreator.systemPrompt}]\n\n${newMessages[0]?.content || ''}` },
      ...newMessages.slice(1).map(m => ({ role: m.role, content: m.content })),
    ];

    try {
      const reply = await callLLM(apiMessages, activeCreator.systemPrompt);
      const updated = [...newMessages, { role: 'assistant' as const, content: reply }];
      setMessages(updated);
      // Check if SKILL.md was produced
      const md = extractSkillMd(reply);
      if (md) setExtractedMd(md);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, I hit an error. Try again or switch to manual entry.' }]);
    }
    setLlmLoading(false);
  }

  function useExtractedSkill() {
    if (!extractedMd) return;
    prefillReview(extractedMd);
    setScreen('review');
  }

  // ── Upload ────────────────────────────────────────────────────────────────
  function handleFile(file: File) {
    setUploadError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      prefillReview(text);
      setScreen('review');
    };
    reader.onerror = () => setUploadError('Failed to read file.');
    reader.readAsText(file);
  }

  // ── GitHub import ──────────────────────────────────────────────────────────
  async function importFromGithub() {
    setGhError(null);
    if (!ghUrl.trim()) return;
    setGhLoading(true);
    try {
      // Convert github.com URL to raw.githubusercontent.com
      let raw = ghUrl
        .replace('github.com', 'raw.githubusercontent.com')
        .replace('/blob/', '/');
      if (!raw.includes('raw.githubusercontent.com')) raw = ghUrl;
      const res = await fetch(raw);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      prefillReview(text);
      setScreen('review');
    } catch (e: any) {
      setGhError('Could not fetch that URL. Make sure it points to a raw SKILL.md file.');
    }
    setGhLoading(false);
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!reviewName.trim() || !reviewBody.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        name: reviewName.trim(),
        description: reviewDesc,
        body: reviewBody,
        domain: reviewDomain || undefined,
        tags: tagList,
        isPublic: false,
      };
      let result;
      if (initial && (initial._id || initial.id)) {
        result = await userSkillsAPI.update(initial._id || initial.id, payload);
      } else {
        result = await userSkillsAPI.create(payload);
      }
      onCreated?.(result);
      onClose();
    } catch (e: any) {
      setSaveError(e?.message || 'Failed to save skill.');
    }
    setSaving(false);
  }

  function addTag(val: string) {
    const t = val.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (t && !tagList.includes(t)) setTagList(prev => [...prev, t]);
    setTagInput('');
  }

  if (!isOpen) return null;

  if (!isOpen) return null;

  // ════════════════════════════════════════════════════════════════════════════
  // SCREEN: MENU
  // ════════════════════════════════════════════════════════════════════════════
  if (screen === 'menu') return (
    <Modal>
      <Header title="Skills" />

      {/* Tabs row */}
      <div style={{ padding: '12px 20px 0', display: 'flex', gap: 8, borderBottom: `0.5px solid ${T.border}` }}>
        {['Official', 'Personal'].map(tab => (
          <button key={tab} style={{
            padding: '6px 14px 10px', fontSize: 13, cursor: 'pointer',
            background: 'none', border: 'none',
            borderBottom: tab === 'Personal' ? `2px solid ${T.ink}` : '2px solid transparent',
            color: tab === 'Personal' ? T.ink : T.ink4,
            fontWeight: tab === 'Personal' ? 500 : 400,
          }}>
            {tab}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>

        {/* Create with AI section */}
        <div style={{ padding: '4px 20px 8px' }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: T.ink6, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Create with AI
          </div>
          {CREATORS.map((c, i) => (
            <button key={c.id} onClick={() => startCreator(c)} style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 12,
              padding: '9px 12px', borderRadius: 8, border: 'none', background: 'none',
              cursor: 'pointer', textAlign: 'left', transition: 'background .12s',
            }}
              onMouseEnter={e => (e.currentTarget.style.background = T.surface)}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              <IconBox creator={c} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: T.ink }}>{c.label} style</div>
                <div style={{ fontSize: 11, color: T.ink4 }}>{c.style}</div>
              </div>
              <ChevronRight size={14} color={T.ink6} />
            </button>
          ))}
        </div>

        <Divider />

        {/* Other options */}
        <div style={{ padding: '8px 20px 4px' }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: T.ink6, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }}>
            Import
          </div>

          {/* Upload */}
          <button onClick={() => setScreen('upload')} style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
            padding: '9px 12px', borderRadius: 8, border: 'none', background: 'none',
            cursor: 'pointer', textAlign: 'left',
          }}
            onMouseEnter={e => (e.currentTarget.style.background = T.surface)}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <div style={{ width: 32, height: 32, borderRadius: 8, background: T.surface, border: `0.5px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Upload size={14} color={T.ink4} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: T.ink }}>Upload a skill</div>
              <div style={{ fontSize: 11, color: T.ink4 }}>Import a SKILL.md file from your computer</div>
            </div>
            <ChevronRight size={14} color={T.ink6} />
          </button>

          {/* GitHub */}
          <button onClick={() => setScreen('github')} style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
            padding: '9px 12px', borderRadius: 8, border: 'none', background: 'none',
            cursor: 'pointer', textAlign: 'left',
          }}
            onMouseEnter={e => (e.currentTarget.style.background = T.surface)}
            onMouseLeave={e => (e.currentTarget.style.background = 'none')}
          >
            <div style={{ width: 32, height: 32, borderRadius: 8, background: T.surface, border: `0.5px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Github size={14} color={T.ink4} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: T.ink }}>Import from GitHub</div>
              <div style={{ fontSize: 11, color: T.ink4 }}>Paste a link to a SKILL.md on GitHub</div>
            </div>
            <ChevronRight size={14} color={T.ink6} />
          </button>
        </div>
      </div>
    </Modal>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SCREEN: CHAT
  // ════════════════════════════════════════════════════════════════════════════
  if (screen === 'chat' && activeCreator) return (
    <Modal wide>
      <Header title={`Create with ${activeCreator.label}`} onBack={() => setScreen('menu')} />

      {/* Creator badge */}
      <div style={{ padding: '10px 20px', borderBottom: `0.5px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
        <IconBox creator={activeCreator} />
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: T.ink }}>{activeCreator.label} skill format</div>
          <div style={{ fontSize: 11, color: T.ink4 }}>{activeCreator.desc}</div>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{
              maxWidth: '82%', padding: '9px 13px', borderRadius: m.role === 'user' ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
              background: m.role === 'user' ? T.greenDark : T.surface,
              color: m.role === 'user' ? 'var(--app-primary-soft)' : T.ink,
              fontSize: 13, lineHeight: 1.55,
              border: m.role === 'assistant' ? `0.5px solid ${T.border}` : 'none',
              whiteSpace: 'pre-wrap',
            }}>
              {m.content}
            </div>
          </div>
        ))}
        {llmLoading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: T.ink4, fontSize: 12 }}>
            <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
            Thinking…
          </div>
        )}
        {extractedMd && (
          <div style={{ background: T.greenLight, border: `0.5px solid #9FE1CB`, borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: T.greenText, marginBottom: 6 }}>✓ SKILL.md ready</div>
            <div style={{ fontSize: 11, color: T.greenText, marginBottom: 10 }}>Your skill was generated. Review and save it.</div>
            <button onClick={useExtractedSkill} style={{
              padding: '6px 14px', fontSize: 12, borderRadius: 7, cursor: 'pointer',
              background: T.greenDark, color: 'var(--app-primary-soft)', border: 'none', fontWeight: 500,
            }}>
              Review & Save →
            </button>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '12px 20px', borderTop: `0.5px solid ${T.border}`, display: 'flex', gap: 8 }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
          placeholder="Message…"
          disabled={llmLoading}
          autoFocus
          style={{
            flex: 1, height: 36, padding: '0 12px', fontSize: 13,
            border: `0.5px solid ${T.border}`, borderRadius: 8,
            background: T.surface, color: T.ink, outline: 'none',
          }}
        />
        <button onClick={sendMessage} disabled={llmLoading || !input.trim()} style={{
          width: 36, height: 36, borderRadius: 8, border: 'none', cursor: 'pointer',
          background: input.trim() ? T.greenDark : T.surface,
          color: input.trim() ? 'var(--app-primary-soft)' : T.ink6,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all .12s',
        }}>
          <Send size={14} />
        </button>
      </div>
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </Modal>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SCREEN: UPLOAD
  // ════════════════════════════════════════════════════════════════════════════
  if (screen === 'upload') return (
    <Modal>
      <Header title="Upload a skill" onBack={() => setScreen('menu')} />
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => e.preventDefault()}
          onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
          style={{
            border: `1.5px dashed ${T.border}`, borderRadius: 12,
            padding: '32px 20px', textAlign: 'center', cursor: 'pointer',
            background: T.surface, transition: 'border-color .15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.borderColor = T.borderHover)}
          onMouseLeave={e => (e.currentTarget.style.borderColor = T.border)}
        >
          <Upload size={22} color={T.ink4} style={{ margin: '0 auto 10px' }} />
          <div style={{ fontSize: 13, fontWeight: 500, color: T.ink, marginBottom: 4 }}>Drop a SKILL.md file here</div>
          <div style={{ fontSize: 12, color: T.ink4 }}>or click to browse</div>
        </div>
        <input ref={fileInputRef} type="file" accept=".md,text/markdown,text/plain" onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])} style={{ display: 'none' }} />
        {uploadError && <div style={{ fontSize: 12, color: T.redText, background: T.red, borderRadius: 7, padding: '8px 12px' }}>{uploadError}</div>}
      </div>
    </Modal>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SCREEN: GITHUB
  // ════════════════════════════════════════════════════════════════════════════
  if (screen === 'github') return (
    <Modal>
      <Header title="Import from GitHub" onBack={() => setScreen('menu')} />
      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 13, color: T.ink2, lineHeight: 1.5 }}>
          Paste a link to a <code style={{ background: T.surface, padding: '1px 5px', borderRadius: 4, fontSize: 12 }}>SKILL.md</code> file on GitHub.
        </div>
        <input
          value={ghUrl}
          onChange={e => setGhUrl(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && importFromGithub()}
          placeholder="https://github.com/user/repo/blob/main/skills/my-skill/SKILL.md"
          style={{
            height: 36, padding: '0 12px', fontSize: 12,
            border: `0.5px solid ${T.border}`, borderRadius: 8,
            background: T.surface, color: T.ink, outline: 'none', fontFamily: 'monospace',
          }}
        />
        {ghError && <div style={{ fontSize: 12, color: T.redText, background: T.red, borderRadius: 7, padding: '8px 12px' }}>{ghError}</div>}
        <button onClick={importFromGithub} disabled={ghLoading || !ghUrl.trim()} style={{
          padding: '8px 0', fontSize: 13, borderRadius: 8, cursor: 'pointer',
          background: T.greenDark, color: 'var(--app-primary-soft)', border: 'none', fontWeight: 500,
          opacity: ghUrl.trim() ? 1 : 0.5,
        }}>
          {ghLoading ? 'Importing…' : 'Import skill'}
        </button>
      </div>
    </Modal>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // SCREEN: REVIEW & SAVE
  // ════════════════════════════════════════════════════════════════════════════
  const DOMAINS = ['Research','Coding','Marketing','Automation','Finance','Design','Social','Documents','Sales','Support','Legal','Operations','Healthcare','HR','Founders','Data'];

  return (
    <Modal wide>
      <Header title={initial ? 'Edit skill' : 'Review skill'} onBack={screen === 'review' && !initial ? () => setScreen('menu') : undefined} />

      <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Name */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: T.ink4, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>Name *</label>
            <input
              value={reviewName}
              onChange={e => setReviewName(e.target.value.toLowerCase().replace(/\s/g, '-').replace(/[^a-z0-9-]/g, ''))}
              placeholder="my-skill-name"
              style={{ width: '100%', height: 36, padding: '0 12px', fontSize: 13, border: `0.5px solid ${T.border}`, borderRadius: 8, background: T.surface, color: T.ink, outline: 'none', fontFamily: 'monospace' }}
            />
            {reviewName && <div style={{ fontSize: 11, color: T.ink4, marginTop: 4 }}>lyra.skills / {reviewName}</div>}
          </div>

          {/* Description */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: T.ink4, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>Description</label>
            <div style={{ position: 'relative' }}>
              <textarea
                value={reviewDesc}
                onChange={e => setReviewDesc(e.target.value.slice(0, 1024))}
                rows={2}
                placeholder="When and what this skill does — this is the trigger signal for the agent"
                style={{ width: '100%', padding: '8px 12px', fontSize: 13, border: `0.5px solid ${reviewDesc.length > 1000 ? '#D85A30' : T.border}`, borderRadius: 8, background: T.surface, color: T.ink, outline: 'none', resize: 'vertical', lineHeight: 1.5 }}
              />
              <div style={{ position: 'absolute', bottom: 6, right: 8, fontSize: 10, color: reviewDesc.length > 1000 ? '#D85A30' : T.ink6 }}>
                {reviewDesc.length} / 1024
              </div>
            </div>
          </div>

          {/* Body */}
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: T.ink4, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>Skill body (SKILL.md)</label>
            <textarea
              value={reviewBody}
              onChange={e => setReviewBody(e.target.value)}
              rows={10}
              placeholder="Full SKILL.md contents — paste or edit here"
              style={{ width: '100%', padding: '8px 12px', fontSize: 12, border: `0.5px solid ${T.border}`, borderRadius: 8, background: T.surface, color: T.ink, outline: 'none', resize: 'vertical', lineHeight: 1.6, fontFamily: 'monospace' }}
            />
          </div>

          {/* Domain + Tags row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: T.ink4, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>Domain</label>
              <select
                value={reviewDomain}
                onChange={e => setReviewDomain(e.target.value)}
                style={{ width: '100%', height: 36, padding: '0 10px', fontSize: 13, border: `0.5px solid ${T.border}`, borderRadius: 8, background: T.surface, color: reviewDomain ? T.ink : T.ink4, outline: 'none' }}
              >
                <option value="">Select domain…</option>
                {DOMAINS.map(d => <option key={d} value={d.toLowerCase()}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: T.ink4, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>Tags</label>
              <input
                value={tagInput}
                onChange={e => setTagInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(tagInput); } }}
                placeholder="Type + Enter to add"
                style={{ width: '100%', height: 36, padding: '0 12px', fontSize: 13, border: `0.5px solid ${T.border}`, borderRadius: 8, background: T.surface, color: T.ink, outline: 'none' }}
              />
            </div>
          </div>

          {/* Tag pills */}
          {tagList.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {tagList.map(tag => (
                <span key={tag} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 99, fontSize: 11, background: T.surface, color: T.ink4, border: `0.5px solid ${T.border}` }}>
                  {tag}
                  <button onClick={() => setTagList(prev => prev.filter(t => t !== tag))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.ink6, padding: 0, lineHeight: 1 }}>×</button>
                </span>
              ))}
            </div>
          )}

          {saveError && (
            <div style={{ fontSize: 12, color: T.redText, background: T.red, borderRadius: 8, padding: '9px 12px' }}>{saveError}</div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 20px', borderTop: `0.5px solid ${T.border}`, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <button onClick={onClose} style={{ padding: '7px 16px', fontSize: 13, borderRadius: 8, border: `0.5px solid ${T.border}`, background: T.bg, color: T.ink4, cursor: 'pointer' }}>
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !reviewName.trim() || !reviewBody.trim()}
          style={{ padding: '7px 18px', fontSize: 13, borderRadius: 8, border: 'none', background: reviewName.trim() && reviewBody.trim() ? T.greenDark : T.surface, color: reviewName.trim() && reviewBody.trim() ? 'var(--app-primary-soft)' : T.ink6, cursor: 'pointer', fontWeight: 500, opacity: saving ? 0.7 : 1 }}
        >
          {saving ? 'Saving…' : initial ? 'Save changes' : 'Save skill'}
        </button>
      </div>
    </Modal>
  );
};

export default AddSkillModal;